import styled, { keyframes } from 'styled-components';

// Shared building blocks for the affiliate-program admin pages (Affiliates,
// Payouts, Analytics, Demos). Ported from the standalone affiliates.zona.miami
// portal (zona-affiliate-admin) so all four pages stay visually consistent
// with each other after moving into the main admin panel.
export const colors = {
  bg: '#050505',
  bgCard: '#0b0b0b',
  bgHover: 'rgba(255, 255, 255, 0.04)',
  bgInput: 'rgba(255, 255, 255, 0.045)',
  surface1: 'rgba(255,255,255,0.035)',
  surface2: 'rgba(255,255,255,0.055)',
  border: 'rgba(255, 255, 255, 0.06)',
  borderLight: 'rgba(255, 255, 255, 0.04)',
  primary: '#4F30EA',
  brand: '#7229F5',
  textSecondary: 'rgba(255, 255, 255, 0.72)',
  textMuted: 'rgba(255, 255, 255, 0.46)',
  textFaint: 'rgba(255, 255, 255, 0.28)',
  success: '#10B981',
  danger: '#EF4444',
  warning: '#F59E0B',
  info: '#0A3FD6',
  successBg: 'rgba(16, 185, 129, 0.1)',
  dangerBg: 'rgba(239, 68, 68, 0.08)',
  warningBg: 'rgba(245, 158, 11, 0.1)',
  infoBg: 'rgba(10, 63, 214, 0.15)',
};

export const PageTitle = styled.h1`
  font-size: 22px; font-weight: 800; color: #fff; letter-spacing: -0.4px;
  margin: 4px 0 2px;
`;
export const PageSub = styled.p`
  font-size: 13px; color: ${colors.textMuted}; margin: 0 0 20px;
`;
export const Head = styled.div`
  display: flex; align-items: flex-end; justify-content: space-between;
  gap: 12px; margin-bottom: 6px;
`;

export const StatGrid = styled.div`
  display: grid; gap: 10px; margin-bottom: 22px;
  grid-template-columns: repeat(2, 1fr);
  @media (min-width: 640px) { grid-template-columns: repeat(4, 1fr); }
`;
export const Tile = styled.div`
  background: ${colors.bgCard};
  border: 1px solid ${colors.border};
  border-radius: 14px;
  padding: 14px 16px;
`;
export const TileNum = styled.div`
  font-size: 22px; font-weight: 800; color: #fff; letter-spacing: -0.5px;
`;
export const TileLabel = styled.div`
  font-size: 11px; color: ${colors.textMuted}; margin-top: 3px;
  text-transform: uppercase; letter-spacing: 0.6px;
`;

export const Card = styled.div`
  background: ${colors.bgCard};
  border: 1px solid ${colors.border};
  border-radius: 14px;
  overflow: hidden;
`;

export const TableWrap = styled.div`
  width: 100%; overflow-x: auto;
`;
export const Table = styled.table`
  width: 100%; border-collapse: collapse; min-width: 640px;
  th, td { text-align: left; padding: 12px 14px; font-size: 13px; white-space: nowrap; }
  th {
    color: ${colors.textMuted}; font-weight: 600; font-size: 11px;
    text-transform: uppercase; letter-spacing: 0.6px;
    border-bottom: 1px solid ${colors.border};
  }
  td { color: ${colors.textSecondary}; border-bottom: 1px solid ${colors.borderLight}; }
  tr:last-child td { border-bottom: none; }
  tbody tr { transition: background 0.12s; }
  tbody tr:hover { background: ${colors.bgHover}; }
  td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
  td.strong { color: #fff; font-weight: 600; }
`;

export const Pill = styled.span`
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 11px; font-weight: 600; padding: 3px 9px; border-radius: 100px;
  ${({ tone }) =>
    tone === 'green' ? `color:${colors.success};background:${colors.successBg};` :
    tone === 'amber' ? `color:${colors.warning};background:${colors.warningBg};` :
    tone === 'red' ? `color:${colors.danger};background:${colors.dangerBg};` :
    tone === 'blue' ? `color:${colors.info};background:${colors.infoBg};` :
    `color:${colors.textMuted};background:${colors.surface1};`}
`;

export const Btn = styled.button`
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  height: 34px; padding: 0 14px; border-radius: 9px; cursor: pointer;
  font-size: 13px; font-weight: 600; border: 1px solid transparent;
  transition: transform 120ms ease, opacity 120ms ease, background 120ms ease;
  &:disabled { opacity: 0.45; cursor: not-allowed; }
  &:hover:not(:disabled) { transform: translateY(-1px); }
  ${({ variant }) =>
    variant === 'primary' ? `background:${colors.primary};color:#fff;` :
    variant === 'danger' ? `background:${colors.dangerBg};color:${colors.danger};border-color:rgba(239,68,68,0.2);` :
    `background:${colors.surface1};color:${colors.textSecondary};border-color:${colors.border};`}
`;

const spin = keyframes`to { transform: rotate(360deg); }`;
export const Spinner = styled.div`
  width: 22px; height: 22px; border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.12);
  border-top-color: ${colors.primary};
  animation: ${spin} 0.7s linear infinite;
  margin: 48px auto;
`;

export const Empty = styled.div`
  text-align: center; color: ${colors.textMuted};
  font-size: 14px; padding: 48px 20px;
`;

export const Avatar = styled.div`
  width: 30px; height: 30px; border-radius: 50%; flex-shrink: 0;
  background: ${({ src }) => (src ? `url(${src}) center/cover` : colors.surface2)};
  border: 1px solid ${colors.border};
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700; color: ${colors.textMuted};
`;

export const NameCell = styled.div`
  display: inline-flex; align-items: center; gap: 10px;
  span.n { color: #fff; font-weight: 600; }
  span.code { color: ${colors.primary}; font-size: 11px; font-weight: 700; letter-spacing: 0.5px; }
`;

export function statusPill(status) {
  if (status === 'approved' || status === 'paid') return 'green';
  if (status === 'pending' || status === 'requested' || status === 'processing') return 'amber';
  if (status === 'denied' || status === 'rejected' || status === 'void' || status === 'failed' || status === 'reversed') return 'red';
  if (status === 'available') return 'green';
  return 'muted';
}

// Cents-accurate, two decimals: payout amounts, confirms and toasts.
export const money = (cents) =>
  ((cents || 0) / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Payout rows, shared by the Payouts page and the Affiliates drawer.
const METHOD_LABEL = { stripe: 'Stripe, bank deposit', zelle: 'Zelle', paypal: 'PayPal', bank: 'Bank transfer', manual: 'Not given' };
const methodKey = (m) => String(m || '').toLowerCase();
export const isStripe = (r) => methodKey(r?.method) === 'stripe';
export const methodLabel = (m) => METHOD_LABEL[methodKey(m)] || m || 'Not given';

// What Stripe clawed back and an admin hasn't put back yet. Never on a failed or rejected row:
// that money was already released to the balance (the server flags those needs_review instead).
export const restorableCents = (r) =>
  ['failed', 'rejected'].includes(r?.status)
    ? 0
    : Math.max((r?.amount_reversed_cents || 0) - (r?.restored_cents || 0), 0);
// Fully restored: status restored and nothing left to put back.
export const isRestored = (r) => r?.status === 'restored' && restorableCents(r) === 0;
export const payoutStatusText = (r) => (isRestored(r) ? 'Restored' : r?.status);

// A processing Stripe payout can be retried or released only 24h after its last send attempt
// (the server's rule; it sends can_retry, this is the fallback when that field is missing).
const RETRY_AFTER_MS = 24 * 60 * 60 * 1000;
export const canRetryPayout = (r, now = Date.now()) => {
  if (typeof r?.can_retry === 'boolean') return r.can_retry;
  if (r?.status !== 'processing') return false;
  const last = r.last_attempt_at || r.processing_at || r.first_attempt_at;
  if (!last) return true;
  const t = new Date(last).getTime();
  // An unreadable date is not retryable (the server's rule).
  return !Number.isNaN(t) && now - t >= RETRY_AFTER_MS;
};
